import React, { Suspense, useLayoutEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { SgdsMasthead } from '@govtechsg/sgds-web-component/react';
import { SpinningLoader } from 'br2s-ui-componentlibrary_r2_02';
import {
  GenericError,
  HTTPErrorAlertContainer,
  NoNetwork,
  UnsupportedBreakpoint,
} from 'br2s-ui-common-utils-02';
import { useStore } from '@/redux/store';
import MockLogin from '@/pages/MockLogin/MockLogin';
import Header from '@/components/Header/Header';
import Footer from '@/components/Footer/Footer';
import { getMockLoginAuth } from '@/services/localService/localService';
import './Layout.scss';
import { CRM_PUBLIC_PAGES } from '@/utilities/constants';
import { PAGE_PATH } from '@/utilities/constants/content';
import TokenManager from '@/components/TokenManager/TokenManager';
import Loading from '@/components/Loading';
import SfvGuard from '@/components/SfvGuard/SfvGuard';
import SessionManager from '@/components/SessionManager/SessionManager';

const NoticeBannerModule = React.lazy(
  () => import('content/NoticeBannerModule'),
);

function MFEOutlet({ isOnline }: { isOnline: boolean }) {
  return (
    <SfvGuard>
      <div id="outlet-container">
        {isOnline ? (
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        ) : (
          <NoNetwork />
        )}
      </div>
    </SfvGuard>
  );
}

function Layout() {
  const { app } = useStore();
  const location = useLocation();
  const isMockAuthenticated = getMockLoginAuth();
  const [isOnline, setIsOnline] = useState(true);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const errorFallbackRender = () => <GenericError />;

  const toggleSearch = () => {
    if (location.pathname.includes('/search')) return;
    setIsSearchOpen((open) => !open);
  };

  // istanbul ignore next
  useLayoutEffect(() => {
    setIsOnline(navigator.onLine);
  }, [location.pathname]);

  if (
    process.env.REACT_APP_SHOW_MOCK_LOGIN?.toString() === 'true' &&
    !isMockAuthenticated &&
    !location.pathname.includes(CRM_PUBLIC_PAGES.PATH)
  )
    return <MockLogin />;

  if (location.pathname.includes(CRM_PUBLIC_PAGES.PATH)) {
    return <Outlet />;
  }

  return (
    <SessionManager>
      <UnsupportedBreakpoint />
      {app.status === 'loading' && <SpinningLoader isFullScreen />}
      <div id="masthead-container" className="masthead-container">
        <div className="container">
          <SgdsMasthead />
        </div>
      </div>
      <Suspense fallback={<></>}>
        <NoticeBannerModule contentPath={PAGE_PATH.NOTICE_BANNER} />
      </Suspense>
      <Header isSearchOpen={isSearchOpen} toggleSearch={toggleSearch} />
      <div
        className="searchsg-container"
        style={{ display: isSearchOpen ? 'block' : 'none' }}
      >
        <div className="container">
          <div
            id="searchsg-searchbar"
            data-client-id={`${process.env.REACT_APP_SITE_SEARCH_CLIENT_ID}`}
          />
        </div>
      </div>
      <div ref={containerRef} className="mfe-container" id="mfe-container">
        <ReactErrorBoundary
          fallbackRender={errorFallbackRender}
          onError={(error) => console.error('Uncaught error:', error)}
          resetKeys={[location.pathname]}
        >
          <HTTPErrorAlertContainer />
          <MFEOutlet isOnline={isOnline} />
        </ReactErrorBoundary>
      </div>
      <TokenManager />
      <Footer containerRef={containerRef} />
    </SessionManager>
  );
}

export default Layout;
